import React, { useEffect, useCallback, useRef, useMemo, Suspense, startTransition } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'

import { CSSTransition, TransitionGroup } from 'react-transition-group'

import * as actions from './game.actions'
import * as selectors from './game.selectors'
import * as loaderSelectors from '../Loader/loader.selectors'
import { useGameUI, useViewport } from '../hooks/useGame'

import AudioContainer from './AudioContainer'
import GameError from './GameError'
import SceneContainer from './SceneContainer'
import ScreensContainer from './ScreensContainer'
import PopupsContainer from './PopupsContainer'

import MarsLoader from '../../Mars/MarsLoader'

/**
 * Loading fallback component for Suspense
 */
const GameLoadingFallback = () => (
  <div className="game-loading-fallback" style={{
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: 'white',
    fontSize: '18px',
    fontWeight: 'bold'
  }}>
    Cargando componentes del juego...
  </div>
)

/**
 *  Main game container
 *  Here live all other containers. Its main responsability is to control the
 *  scene change process and register/unregister resize event listener.
 */
const Game = () => {
  const dispatch = useDispatch()

  // Usar los nuevos hooks de contexto para UI y viewport
  const { ui, actions: uiActions } = useGameUI()
  const { viewport, isMobile, isDesktop } = useViewport()

  // Redux selectors optimizados con memoización (mantenemos compatibilidad)
  const gameState = useSelector(
    (state) => ({
      showingError: selectors.isShowingError(state),
      showingLoaderScreen: loaderSelectors.isLoading(state),
      progress: loaderSelectors.getProgress(state),
      showLoadingBar: loaderSelectors.getShowLoadingBar(state),
    }),
    shallowEqual
  )

  // Destructure optimized state
  const { showingError, showingLoaderScreen, progress, showLoadingBar } = gameState

  // Ref for CSSTransition to avoid findDOMNode warning
  const transitionRef = useRef(null)

  // Action creators memoizados para mejor rendimiento con startTransition
  const startResize = useCallback(() => {
    startTransition(() => {
      dispatch(actions.startResize())
    })
  }, [dispatch])
  
  const broadcastUniversalClick = useCallback(
    (target) => {
      startTransition(() => {
        dispatch(actions.broadcastUniversalClick(target))
      })
    },
    [dispatch]
  )
  const broadcastUniversalTouch = useCallback(
    (target) => {
      startTransition(() => {
        dispatch(actions.broadcastUniversalTouch(target))
      })
    },
    [dispatch]
  )
  const broadcastUniversalTouchEnd = useCallback(
    (target) => {
      startTransition(() => {
        dispatch(actions.broadcastUniversalTouchEnd(target))
      })
    },
    [dispatch]
  )
  const broadcastUniversalMouseLeave = useCallback(
    (target) => {
      startTransition(() => {
        dispatch(actions.broadcastUniversalMouseLeave(target))
      })
    },
    [dispatch]
  )
  const broadcastUniversalMouseUp = useCallback(
    (target) => {
      startTransition(() => {
        dispatch(actions.broadcastUniversalMouseUp(target))
      })
    },
    [dispatch]
  )
  const broadcastUniversalMouseDown = useCallback(
    (target) => {
      startTransition(() => {
        dispatch(actions.broadcastUniversalMouseDown(target))
      })
    },
    [dispatch]
  )
  const broadcastUniversalDrop = useCallback(
    (target) => {
      startTransition(() => {
        dispatch(actions.broadcastUniversalDrop(target))
      })
    },
    [dispatch]
  )
  const broadcastUniversalDragEnd = useCallback(
    (target) => {
      startTransition(() => {
        dispatch(actions.broadcastUniversalDragEnd(target))
      })
    },
    [dispatch]
  )

  // useEffect para manejar resize events
  useEffect(() => {
    const handleResize = () => {
      startResize()
    }

    // Agregar event listener al montar
    window.addEventListener('resize', handleResize, true)

    // Cleanup function para remover event listener al desmontar
    return () => {
      window.removeEventListener('resize', handleResize, true)
    }
  }, [startResize])

  /**
   *  Universal onClick handler with modern UI context.
   */
  const onClick = useCallback(
    (event) => {
      // Actualizar posición del cursor en el contexto
      uiActions.updateCursorPosition(event.clientX, event.clientY)
      
      // Broadcast tradicional para compatibilidad
      broadcastUniversalClick(event.target)
    },
    [broadcastUniversalClick, uiActions]
  )

  /**
   *  Universal onTouchStart handler.
   */
  const onTouchStart = useCallback(
    (event) => {
      broadcastUniversalTouch(event.target)
    },
    [broadcastUniversalTouch]
  )

  /**
   *  Universal onTouchEnd handler.
   */
  const onTouchEnd = useCallback(
    (event) => {
      broadcastUniversalTouchEnd(event.target)
    },
    [broadcastUniversalTouchEnd]
  )

  const onMouseDown = useCallback(
    (event) => {
      broadcastUniversalMouseDown(event.target)
    },
    [broadcastUniversalMouseDown]
  )

  const onMouseUp = useCallback(
    (event) => {
      broadcastUniversalMouseUp(event.target)
    },
    [broadcastUniversalMouseUp]
  )

  const onMouseLeave = useCallback(
    (event) => {
      broadcastUniversalMouseLeave(event.target)
    },
    [broadcastUniversalMouseLeave]
  )

  const onDropped = useCallback(
    (event) => {
      broadcastUniversalDrop(event.target)
    },
    [broadcastUniversalDrop]
  )

  const onDragEnd = useCallback(
    (event) => {
      broadcastUniversalDragEnd(event.target)
    },
    [broadcastUniversalDragEnd]
  )

  // Memoized styles para evitar recreación en cada render
  const transitionContainerStyle = useMemo(
    () => ({
      position: 'absolute',
      height: '100%',
      width: '100%',
    }),
    []
  )

  // CSS classes dinámicas basadas en el contexto
  const gameContainerClasses = useMemo(() => {
    const classes = ['game-container']
    
    if (isMobile) classes.push('game-container--mobile')
    if (isDesktop) classes.push('game-container--desktop')
    if (ui.dragState.isDragging) classes.push('game-container--dragging')
    if (ui.modal.isOpen) classes.push('game-container--modal-open')
    
    return classes.join(' ')
  }, [isMobile, isDesktop, ui.dragState.isDragging, ui.modal.isOpen])

  // Renderizado condicional al final, después de todos los hooks
  if (showingError) {
    return <GameError />
  }

  return (
    <div
      className={gameContainerClasses}
      onClick={onClick}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
      onDrop={onDropped}
      onDragEnd={onDragEnd}
    >
      {!showingLoaderScreen && (
        <div className="game-scene-container">
          <Suspense fallback={<GameLoadingFallback />}>
            <SceneContainer />
            <ScreensContainer />
            <PopupsContainer />
          </Suspense>
        </div>
      )}

      <CSSTransition classNames="transition-scene" in={showingLoaderScreen} timeout={500} nodeRef={transitionRef}>
        <div ref={transitionRef}>
          {!showingLoaderScreen ? (
            <span />
          ) : (
            <TransitionGroup key={'transitionSceneFade'} style={transitionContainerStyle}>
              <Suspense fallback={<GameLoadingFallback />}>
                <MarsLoader progress={progress} showLoadingBar={showLoadingBar} />
                <PopupsContainer />
              </Suspense>
            </TransitionGroup>
          )}
        </div>
      </CSSTransition>

      <Suspense fallback={<GameLoadingFallback />}>
        <AudioContainer />
      </Suspense>
    </div>
  )
}

// Exportar componente optimizado con React.memo
export default React.memo(Game)
